import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { StaticRow, ToggleRow } from './cabinetColumnCustomizerDraggableRow';
import { matchesSearchTerm } from '../../utils/cabinetColumnUtils';

/**
 * Lets a user add filters backed by indexes the cabinet does not map, and remove the
 * ones they added. The cabinet's own filters are always shown and cannot be removed.
 */
const CabinetFilterCustomizer = ({
  isOpen,
  staticFilters,
  candidateFilters,
  addedFilterIndexes,
  onSave,
  onClose
}) => {
  const [pendingIndexes, setPendingIndexes] = useState(addedFilterIndexes);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  // Sync draft state when the modal opens. Do not depend on addedFilterIndexes while
  // open — a new empty-array identity from parent memos would wipe in-progress toggles.
  useEffect(() => {
    if (isOpen) {
      setPendingIndexes(addedFilterIndexes);
      setSearchTerm('');
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open transition only
  }, [isOpen]);
  const filterByIndex = useMemo(() => {
    const map = {};
    candidateFilters.forEach(filter => {
      map[filter.index] = filter;
    });
    return map;
  }, [candidateFilters]);
  const filteredStaticFilters = useMemo(() => staticFilters.filter(({
    title
  }) => matchesSearchTerm(title || '', searchTerm)), [staticFilters, searchTerm]);
  const shownFilters = useMemo(() => pendingIndexes.map(index => filterByIndex[index] || {
    index,
    title: index
  }).filter(({
    title
  }) => matchesSearchTerm(title, searchTerm)), [pendingIndexes, filterByIndex, searchTerm]);
  const hiddenFilters = useMemo(() => candidateFilters.filter(({
    index
  }) => !pendingIndexes.includes(index)).filter(({
    title
  }) => matchesSearchTerm(title, searchTerm)), [candidateFilters, pendingIndexes, searchTerm]);
  const handleToggleOn = index => {
    setPendingIndexes(prev => prev.includes(index) ? prev : [...prev, index]);
  };
  const handleToggleOff = index => {
    setPendingIndexes(prev => prev.filter(current => current !== index));
  };
  const handleHideAll = () => {
    setPendingIndexes([]);
  };

  // saving keeps a second click from persisting the same choice twice
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(pendingIndexes);
    } finally {
      setSaving(false);
    }
  };
  const handleClose = () => {
    setPendingIndexes(addedFilterIndexes);
    setSearchTerm('');
    onClose();
  };
  const hasShownSection = filteredStaticFilters.length > 0 || shownFilters.length > 0;
  const hasHiddenSection = hiddenFilters.length > 0;
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    toggle: handleClose,
    size: "sm",
    className: "t-filter-customizer-modal",
    "data-testid": "t-filter-customizer-modal"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: handleClose,
    className: "border-0 pb-0"
  }, "Filters"), /*#__PURE__*/React.createElement(ModalBody, {
    className: "pt-2 pb-0"
  }, /*#__PURE__*/React.createElement(Input, {
    type: "search",
    placeholder: "Search for filters",
    value: searchTerm,
    onChange: e => setSearchTerm(e.target.value),
    "data-testid": "t-filter-customizer-search",
    className: "mb-3"
  }), /*#__PURE__*/React.createElement("div", {
    className: "t-filter-customizer-scroll",
    style: {
      maxHeight: 'min(60vh, 420px)',
      overflowY: 'auto'
    }
  }, hasShownSection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center justify-content-between px-3 mb-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "small fw-semibold text-muted text-uppercase"
  }, "Shown"), pendingIndexes.length > 0 && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn-link btn-sm p-0 text-decoration-none",
    "data-testid": "t-filter-customizer-hide-all",
    onClick: handleHideAll
  }, "Hide all")), filteredStaticFilters.map(({
    index,
    title
  }) => /*#__PURE__*/React.createElement(StaticRow, {
    key: index || title,
    testIdPrefix: "t-filter-customizer",
    title: title || index || ''
  })), shownFilters.map(({
    index,
    title
  }) => /*#__PURE__*/React.createElement(ToggleRow, {
    key: index,
    testIdPrefix: "t-filter-customizer-shown",
    itemKey: index,
    title: title,
    checked: true,
    onToggle: handleToggleOff
  }))), hasShownSection && hasHiddenSection && /*#__PURE__*/React.createElement("hr", {
    className: "my-2"
  }), hasHiddenSection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "px-3 mb-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "small fw-semibold text-muted text-uppercase"
  }, "Hidden")), hiddenFilters.map(({
    index,
    title
  }) => /*#__PURE__*/React.createElement(ToggleRow, {
    key: index,
    testIdPrefix: "t-filter-customizer-hidden",
    itemKey: index,
    title: title,
    checked: false,
    onToggle: handleToggleOn
  }))), !hasShownSection && !hasHiddenSection && /*#__PURE__*/React.createElement("p", {
    className: "text-muted small px-3 mb-0"
  }, "No filters match your search."))), /*#__PURE__*/React.createElement(ModalFooter, {
    className: "border-0 pt-2"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    "data-testid": "t-filter-customizer-done",
    onClick: handleSave,
    disabled: saving
  }, "Done")));
};
CabinetFilterCustomizer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  staticFilters: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.string,
    title: PropTypes.string
  })).isRequired,
  candidateFilters: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })).isRequired,
  addedFilterIndexes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
export default CabinetFilterCustomizer;